/**
 * 手動グループ作成ダイアログ。
 * 名前 + 説明 + 配信先選択（複数）。
 * 仕様書: docs/01_spec/spec_mail_system.md B-11.3
 */
import { Search, UserPlus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useMailContacts } from '@/hooks/useMailContacts';
import { useCreateMailGroup } from '@/hooks/useMailGroups';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManualGroupDialog({ open, onOpenChange }: Props) {
  // limit を指定しない = 全件取得。差分同期キャッシュ経由なので追加 read コストは発生しない
  const { data: contacts, isLoading } = useMailContacts();
  const createGroup = useCreateMailGroup();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  /** 確定済みの検索キーワード (バッジ表示)。AND で絞り込み */
  const [keywords, setKeywords] = useState<string[]>([]);
  /** 入力欄の未確定文字 */
  const [kwInput, setKwInput] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setKeywords([]);
      setKwInput('');
      setSelected(new Set());
    }
  }, [open]);

  /** カンマ・タブ・改行で複数同時追加にも対応してキーワードを追加 */
  const addKeyword = (raw: string) => {
    const parts = raw
      .split(/[,、\t\n]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (parts.length === 0) return;
    setKeywords((prev) => {
      const next = [...prev];
      for (const p of parts) {
        if (!next.includes(p)) next.push(p);
      }
      return next;
    });
    setKwInput('');
  };

  const removeKeyword = (k: string) => {
    setKeywords((prev) => prev.filter((x) => x !== k));
  };

  const candidates = useMemo(() => {
    const list = contacts ?? [];
    // 入力欄の未確定文字も「有効なキーワード」として一時的に含めて絞り込む
    const pending = kwInput.trim();
    const effective = pending
      ? Array.from(new Set([...keywords, pending]))
      : keywords;
    if (effective.length === 0) return list;
    const lowered = effective.map((k) => k.toLowerCase());

    // 1 件のキーワードに対するマッチ判定 (名前 / メアド / タグ / 会社 / 職業 / 都道府県 / 姓名カナ)
    const matchesOne = (
      c: (typeof list)[number],
      k: string,
    ): boolean => {
      if (c.name?.toLowerCase().includes(k)) return true;
      if (c.emails?.some((e) => e.address.toLowerCase().includes(k))) return true;
      if (c.tags?.some((t) => t.toLowerCase().includes(k))) return true;
      if (c.companyName?.toLowerCase().includes(k)) return true;
      if (c.occupation?.toLowerCase().includes(k)) return true;
      if (c.personalAddress?.prefecture?.toLowerCase().includes(k)) return true;
      if (c.lastName?.toLowerCase().includes(k)) return true;
      if (c.firstName?.toLowerCase().includes(k)) return true;
      if (c.lastNameKana?.toLowerCase().includes(k)) return true;
      if (c.firstNameKana?.toLowerCase().includes(k)) return true;
      return false;
    };
    // 全キーワードが何らかのフィールドにヒットする (AND 絞り込み)
    return list.filter((c) => lowered.every((k) => matchesOne(c, k)));
  }, [contacts, keywords, kwInput]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const c of candidates) next.add(c.id);
      return next;
    });
  };

  const clearAll = () => setSelected(new Set());

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('グループ名を入力してください');
      return;
    }
    if (selected.size === 0) {
      toast.error('1 名以上のメンバーを選択してください');
      return;
    }
    try {
      await createGroup.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        type: 'manual',
        memberContactIds: Array.from(selected),
      });
      toast.success(`グループ「${name.trim()}」を作成しました（${selected.size} 名）`);
      onOpenChange(false);
    } catch (err) {
      toast.error(`作成失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>手動でグループを作成</DialogTitle>
          <DialogDescription>
            グループ名と配信先を選択して作成します。後からメンバー追加・除外も可能です。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">グループ名（必須）</Label>
            <Input
              type="text"
              placeholder="例: 関西エリアの見込み客"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">説明（任意）</Label>
            <Textarea
              placeholder="このグループの用途・対象者など"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          <div>
            <Label className="text-xs">メンバー選択（必須）</Label>
            {/* 複数キーワード入力 (AND 絞り込み)。Enter / カンマで確定→バッジ化、Backspace で末尾削除 */}
            <div className="relative mb-2 flex flex-wrap items-center gap-1 px-2 py-1.5 rounded-sm bg-[var(--bg-input)] border border-[var(--border)] focus-within:border-[var(--border-focus)] transition-colors min-h-[2.5rem] pl-9">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[var(--text-muted)] pointer-events-none" />
              {keywords.map((k) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[var(--accent-subtle)] text-[var(--accent)] border border-[var(--accent)]"
                >
                  {k}
                  <button
                    type="button"
                    onClick={() => removeKeyword(k)}
                    className="hover:text-[var(--accent-red)] -mr-0.5"
                    aria-label={`「${k}」を削除`}
                  >
                    <X className="size-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                placeholder={
                  keywords.length === 0
                    ? '名前 / メアド / タグ / 都道府県 / 職業 / 会社名で検索 (Enter で追加)'
                    : 'キーワードを追加 (AND 絞り込み)'
                }
                value={kwInput}
                onChange={(e) => setKwInput(e.target.value)}
                onKeyDown={(e) => {
                  // IME 変換中の Enter は変換確定に使われるので無視
                  const composing =
                    e.nativeEvent.isComposing || e.keyCode === 229;
                  if (e.key === 'Enter' && !composing) {
                    e.preventDefault();
                    addKeyword(kwInput);
                  } else if (
                    e.key === 'Backspace' &&
                    kwInput === '' &&
                    keywords.length > 0 &&
                    !composing
                  ) {
                    // 入力欄が空 + Backspace → 末尾キーワード削除
                    removeKeyword(keywords[keywords.length - 1]);
                  }
                }}
                onBlur={() => {
                  // フォーカスが外れた瞬間に確定 (IME 環境のフォールバック)
                  if (kwInput.trim()) addKeyword(kwInput);
                }}
                className="flex-1 min-w-[12ch] bg-transparent outline-none text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
              />
            </div>
            <div className="flex items-center justify-between text-xs text-[var(--text-muted)] mb-1.5">
              <span>
                {isLoading
                  ? '読み込み中…'
                  : `${candidates.length} 件 / 選択 ${selected.size} 件`}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllVisible}
                  disabled={candidates.length === 0}
                  className="text-[var(--accent)] hover:underline disabled:opacity-40 disabled:no-underline"
                >
                  表示中を全選択
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  disabled={selected.size === 0}
                  className="text-[var(--text-secondary)] hover:underline disabled:opacity-40 disabled:no-underline"
                >
                  選択解除
                </button>
              </div>
            </div>
            <div className="border border-[var(--border)] rounded max-h-72 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center p-12">
                  <Spinner />
                </div>
              ) : candidates.length === 0 ? (
                <div className="text-xs text-[var(--text-muted)] italic text-center py-12">
                  {keywords.length > 0 || kwInput.trim()
                    ? '一致する配信先がありません'
                    : '配信先がまだ登録されていません'}
                </div>
              ) : (
                <div className="divide-y divide-[var(--border)]">
                  {candidates.map((c) => {
                    const prefecture = c.personalAddress?.prefecture?.trim();
                    const occupation = c.occupation?.trim();
                    const company = c.companyName?.trim();
                    return (
                      <label
                        key={c.id}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-[var(--bg-hover)] cursor-pointer text-xs"
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(c.id)}
                          onChange={() => toggle(c.id)}
                          className="size-3.5 accent-[var(--accent)]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium">
                              {c.name || '（名前なし）'}
                            </span>
                            {company && (
                              <span className="text-[10px] text-[var(--text-secondary)]">
                                {company}
                              </span>
                            )}
                          </div>
                          <div className="text-[var(--text-muted)] font-mono truncate">
                            {c.defaultEmail}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 flex-wrap justify-end">
                          {prefecture && (
                            <span className="text-[10px] bg-[var(--accent-subtle)] text-[var(--accent)] px-1.5 py-0.5 rounded-full">
                              {prefecture}
                            </span>
                          )}
                          {occupation && (
                            <span className="text-[10px] bg-[var(--accent-purple-subtle,_rgba(155,93,229,0.15))] text-[var(--accent-purple,_#9b5de5)] px-1.5 py-0.5 rounded-full">
                              {occupation}
                            </span>
                          )}
                          {c.tags && c.tags.length > 0 && (
                            <>
                              {c.tags.slice(0, 2).map((t) => (
                                <span
                                  key={t}
                                  className="text-[10px] bg-[var(--bg-hover)] px-1.5 py-0.5 rounded-full text-[var(--text-muted)]"
                                >
                                  {t}
                                </span>
                              ))}
                            </>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleCreate}
            disabled={!name.trim() || selected.size === 0 || createGroup.isPending}
          >
            {createGroup.isPending ? (
              <Spinner className="size-3 mr-1" />
            ) : (
              <UserPlus className="size-3.5 mr-1" />
            )}
            グループを作成
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
